import { PaymentProviders } from '@/enums';
import { createAsyncThunk } from '@/redux/createAsyncThunk';
import {
    getLAPaymentCards,
    GetLAPaymentCardsPayload,
    postAddLACard,
    postAddZipCard,
    postUpdateLACard,
} from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.api';
import { shouldFetchBidderCreditCards } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import { SavedCreditCard } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.types';
import { getAuthToken, getBidderId } from '@/redux/modules/account/user/user.selectors';
import { defaultUserSlice } from '@/redux/modules/account/user/user.types';
import { CARD_REAUTH_SUCCESS } from '@/redux/modules/actions';
import { getDeployment } from '@/redux/modules/config';
import { CardBrands, CreditCardAddress as Address } from '@/types/CreditCard';

/**
 * @deprecated If using redux-toolkit's createSlice, use loadHomepageData.pending
 */
export const ADD_ZIP_PAYMENT_CARD_REQUEST = 'la/ui/addZipPaymentCard/REQUEST';
/**
 * If using redux-toolkit's createSlice, use loadHomepageData.fulfilled
 */
export const ADD_ZIP_PAYMENT_CARD_SUCCESS = 'la/ui/addZipPaymentCard/SUCCESS';
/**
 * @deprecated If using redux-toolkit's createSlice, use loadHomepageData.rejected
 */
export const ADD_ZIP_PAYMENT_CARD_FAIL = 'la/ui/addZipPaymentCard/FAIL';

export type FetchLAPaymentCardPayload = Partial<{
    providedBidderId: number;
    token: string;
}>;

export const fetchBidderCreditCards = createAsyncThunk<GetLAPaymentCardsPayload, FetchLAPaymentCardPayload>(
    'la/ui/creditCard/fetchBidderCreditCards',
    async ({ providedBidderId, token }, { getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const storedBidderId = getBidderId(state);

        const bidderId = providedBidderId || storedBidderId;

        // if we don't have a bidderId, we shouldn't try to fetch credit card info
        if (!bidderId || bidderId === defaultUserSlice.userData.bidderId) {
            return;
        }

        const response = await getLAPaymentCards({
            authToken: token || authToken,
            bidderId,
            deployment,
        });
        return response.payload;
    }
);

export const fetchBidderCreditCardsIfNeeded = createAsyncThunk<void, FetchLAPaymentCardPayload | void>(
    'la/ui/creditCard/fetchBidderCreditCardsIfNeeded',
    async (args, { dispatch, getState }) => {
        const state = getState();
        if (shouldFetchBidderCreditCards(state)) {
            await dispatch(fetchBidderCreditCards(args || {}));
        }
    }
);

export type AddBidderCreditCardParams = {
    address: Address;
    cardToken: string;
    cardholderName: {
        first: string;
        last: string;
    };
    houseId: number;
    providerId: PaymentProviders;
};

export const addBidderCreditCard = createAsyncThunk<SavedCreditCard, AddBidderCreditCardParams>(
    'la/ui/creditCard/addBidderCreditCard',
    async (
        { address, cardholderName, cardToken, houseId, providerId = PaymentProviders.Payrix },
        { dispatch, getState }
    ) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);

        const response = await postAddLACard({
            authToken,
            bidderId,
            billingAddress: address,
            cardholderName: `${cardholderName.first} ${cardholderName.last}`,
            cardNonce: cardToken,
            deployment,
            houseId,
            providerId,
        });

        // add card id to list of auth success ids
        dispatch({
            payload: response.payload.id,
            type: CARD_REAUTH_SUCCESS,
        });

        await dispatch(fetchBidderCreditCards({}));

        return response.payload;
    }
);

export type UpdateBidderCreditCardParams = {
    billingAddress?: Address;
    cardId: number;
    defaultCard: boolean;
    enabled: boolean;
};

/**
 * Update is for updating the billing address of an existing card.
 * The API can disable cards too, but the UI has no hookup for that.
 */
export const updateBidderCreditCard = createAsyncThunk<SavedCreditCard, UpdateBidderCreditCardParams>(
    'la/ui/creditCard/updateBidderCreditCard',
    async ({ billingAddress, cardId, defaultCard, enabled }, { dispatch, getState }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);

        const response = await postUpdateLACard({
            authToken,
            bidderId,
            billingAddress,
            cardId,
            defaultCard,
            deployment,
            enabled,
        });

        await dispatch(fetchBidderCreditCards({}));

        return response.payload;
    }
);

export type AddBidderZipCreditCardParams = {
    billingAddress: Address;
    brand: CardBrands;
    firstName: string;
    houseId: number;
    lastName: string;
    month: number;
    number: string;
    providerId: number;
    securityCode: string;
    year: string;
};

export const addBidderZipCreditCard = createAsyncThunk<SavedCreditCard, AddBidderZipCreditCardParams>(
    'la/ui/creditCard/addBidderZipCreditCard',
    async (params, { dispatch, getState }) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const bidderId = getBidderId(state);
            const deployment = getDeployment(state);

            // payment.ts listens to this dispatch, remove this when that is converted
            dispatch({
                payload: bidderId,
                type: ADD_ZIP_PAYMENT_CARD_REQUEST,
            });

            const response = await postAddZipCard({
                ...params,
                authToken,
                bidderId,
                deployment,
            });

            // payment.ts listens to this dispatch, remove this when that is converted
            dispatch({
                meta: { actionTime: Date.now() },
                payload: response.payload,
                type: ADD_ZIP_PAYMENT_CARD_SUCCESS,
            });

            return response.payload;
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: ADD_ZIP_PAYMENT_CARD_FAIL,
            });

            // throw error again for creditCardReducer
            throw error;
        }
    }
);

export const clearCreditCardErrors = createAsyncThunk<void, void>('la/ui/creditCard/clearCreidtCardErrors', () => {});
